import React from "react";
import moment from "moment";
import {
	Accordion,
	AccordionButton,
	AccordionItem,
	AccordionPanel,
	Box,
	Flex,
	SkeletonCircle,
	Text,
} from "@chakra-ui/react";
import SkeletonBase from "newui/components/Skeleton";
import { Icon } from "assets/Icon";
import { LONG_DATE_TIME_FORMAT } from "components/RFQ/SubmissionDeadline/SubmissionDeadline";
import EmailLink from "newui/features/Rfx/components/EmailLink";
import Tooltip from "components/Tooltip";
import { ListHorizontalItem } from "./ListHorizontalItem";
import TextBoxHTML from "newui/components/TextBoxHTML";
import { isDev } from "utils/logging";
import RfxSubmissionStatusPill from "../RfxSubmissionStatusPill";
import FallbackImage from "components/FallbackImage/FallbackImage";
import { cn } from "utils/helpers";
import { useSelector } from "react-redux";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import { useCurrentUser } from "hooks/auth";

interface SubmissionDetailsProps {
	isLoading?: boolean;
	onReload?: () => void;
}

export const SubmissionDetails: React.FC<SubmissionDetailsProps> = ({
	isLoading = false,
	onReload,
}) => {
	const { rfxData, rfxSubmissionData } = useRfxSubmissionData();
	const { isGuest } = useCurrentUser();

	const isSideBarCollapsed = useSelector(
		(state: any) => state.sidebar.sidebarcollapse
	);

	if (isLoading || !rfxData || !rfxSubmissionData) {
		return (
			<div>
				<SkeletonBase
					height="430px"
					width="100%"
				/>
			</div>
		);
	}

	const formattedSubmissionDate = rfxSubmissionData.submittedAt
		? `${moment(rfxSubmissionData.submittedAt).format(LONG_DATE_TIME_FORMAT)} AWST`
		: "Not Submitted";

	const company = rfxSubmissionData.company;
	const submittedBy = rfxSubmissionData.submittedBy;

	//company.description = null;

	const companyDetails = (
		<Flex
			w="full"
			align="center"
			direction={{ base: "column", md: "row" }} // mobile: column, desktop: row
		>
			{/* Logo */}
			<Box
				w={{ base: "30px", sm: "80px", md: "120px" }} // responsive width
				h="auto"
				mr={{ base: 0, md: 4 }}
				mb={{ base: 4, md: 0 }} // add spacing for mobile
				display="flex"
				justifyContent="center"
				alignItems="center"
			>
				{isLoading ? (
					<SkeletonCircle
						size="12"
						startColor="lightgray"
					/>
				) : (
					<FallbackImage
						src={company?.logo_path}
						className="max-h-28 min-w-[30px]"
					/>
				)}
			</Box>

			{/* Project Name & Company */}
			<Box
				flex="1"
				w="full"
			>
				<Text
					fontSize={{ base: "lg", md: "2xl" }}
					textAlign={{ base: "center", md: "left" }}
					className={cn(
						"text-black",
						company?.description
							? { base: "pl-0", md: "pl-[10.5rem]" }
							: { base: "pl-0", md: "pl-[9.5rem]" }
					)}
				>
					{company?.name}
					{company?.description && (
						<span className="mt-1 block text-sm font-normal italic text-gray-500 md:mt-0">
							Click here to show/hide more details
						</span>
					)}
				</Text>
			</Box>
		</Flex>
	);

	const getEmailLink = (email: string | null) => {
		return email ? (
			<EmailLink
				email={email}
				subject={`RE: Submission to ${rfxData?.companyOwner?.name} for ${rfxData?.name}`}
			/>
		) : (
			"-"
		);
	};

	return (
		<div className="relative rounded-lg border border-solid border-[#EAECF0] bg-white p-10 shadow-lg">
			<div className="absolute left-4 top-4 text-sm">{`Submission for: ${rfxData.rfxType?.name} | ${rfxData.name}`}</div>
			{onReload && isDev() && (
				<div className="absolute right-4 top-4 z-50">
					<Tooltip
						label="Reload data"
						placement="top"
						portalProps={{ appendToParentPortal: true }}
					>
						<Icon
							icon="refresh"
							className="cursor-pointer text-gray-500 hover:text-gray-700"
							onClick={onReload}
						/>
					</Tooltip>
				</div>
			)}
			{/* Header */}
			{company?.description ? (
				<Accordion
					allowToggle
					className="pb-4 pt-4"
				>
					<AccordionItem border="none">
						{({ isExpanded }) => (
							<>
								{/* Accordion Header */}
								<Tooltip
									label={
										company?.description
											? "Click here to show/hide more details"
											: ""
									}
									shouldWrapChildren={false}
								>
									<Box
										display="block"
										width="100%"
									>
										<AccordionButton
											className="m-2 rounded-md hover:!bg-blue-100"
											px={0}
											py={0}
											display="flex"
											alignItems="flex-start"
										>
											{companyDetails}
										</AccordionButton>
									</Box>
								</Tooltip>

								{/* Accordion Panel (Expanded Content) */}
								<AccordionPanel pb={4}>
									<Box color="gray.700">
										<TextBoxHTML
											html={company?.description}
											className="text-sm"
										/>
									</Box>
								</AccordionPanel>
							</>
						)}
					</AccordionItem>
				</Accordion>
			) : (
				<div className="pb-4 pt-4">{companyDetails}</div>
			)}
			<div className="h-4">
				<hr />
			</div>
			{/* Details */}
			<div
				className={cn(
					"grid w-full gap-6",
					isSideBarCollapsed
						? "grid-cols-1 sm:grid-cols-2 md:grid-cols-2"
						: "grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2"
				)}
			>
				{/* Other Metadata first */}
				<div className="order-1 flex flex-col gap-2 md:order-1">
					<ListHorizontalItem
						title="Response Status"
						value={
							<RfxSubmissionStatusPill
								submissionStatus={rfxSubmissionData?.status}
								size="p-2"
							/>
						}
					/>
					<ListHorizontalItem
						title="Submitted On"
						value={formattedSubmissionDate}
					/>
					<ListHorizontalItem
						title="Years of Trading"
						value={company?.years_trading}
					/>
					<ListHorizontalItem
						title="Indigenous Business?"
						value={company?.indigenous_business ? "Yes" : "No"}
					/>
				</div>

				{/* Submission Contact */}
				{!isGuest && (
					<div className="order-3 flex flex-col gap-2 md:order-2">
						<div className="flex items-center gap-3 font-medium">
							<Icon icon="user-03" /> <p>Submission Contact Info</p>
						</div>
						<ListHorizontalItem
							title="Name"
							value={submittedBy?.full_name ?? company?.contact_name ?? "-"}
						/>
						<ListHorizontalItem
							title="Email"
							value={getEmailLink(submittedBy?.email ?? company?.email)}
						/>
						<ListHorizontalItem
							title="Phone Number"
							value={submittedBy?.phone ?? company?.phone ?? "-"}
						/>
					</div>
				)}

				{/* Company Contact */}
				{!isGuest && (
					<div className="order-2 flex flex-col gap-2 md:order-3">
						<div className="flex items-center gap-3 font-medium">
							<Icon icon="user-03" /> <p>General Company Contact Info</p>
						</div>
						<ListHorizontalItem
							title="Name"
							value={company?.contact_name ?? "-"}
						/>
						<ListHorizontalItem
							title="Email"
							value={getEmailLink(company?.email)}
						/>
						<ListHorizontalItem
							title="Phone Number"
							value={company?.phone ?? "-"}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
